import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import GuaranteesManagerApplicationSidePanelLayout from '../shared/SidePanel/GuaranteesManagerApplicationSidePanelLayout.tsx';
import GuaranteesManagerStepNavigator from '../stepNavigation/GuaranteesManagerStepNavigator.tsx';
import ManagerApprovalLetterDocumentSection from './ManagerApprovalLetterDocumentSection.tsx';
import { useManagerApprovalLetterPage } from './useManagerApprovalLetterPage.ts';
import { useManagerApprovalLetterPdfPreview } from './useManagerApprovalLetterPdfPreview.ts';

const ManagerApprovalLetterPage: React.FC = () => {
  const {
    contragentId,
    coverageTypeOption,
    coverageTypeOptions,
    currentRouteApplication,
    effectiveApplicationId,
    handleCloseAlertModal,
    handleCloseAmAccessModal,
    handleCloseSuccessModal,
    handleCoverageTypeChange,
    handleMainDealConditionsDirtyChange,
    handleMainDealConditionsSubmit,
    handleSubmitByAmManager,
    headerProps,
    isAmAccessDenied,
    isAmAccessLoading,
    isApplicationLoading,
    isMainDealConditionsLoading,
    isMainDealConditionsSaving,
    isSubmitDisabled,
    isSubmittingByAmManager,
    mainDealConditions,
    modalAlert,
    modalSuccess,
    redirectPath,
    setModalAlert,
    showGuaranteesRequestError,
    showCoverageTypeField,
  } = useManagerApprovalLetterPage();
  const { handleOpenPdf, isDocumentLoading, pdfUrl } = useManagerApprovalLetterPdfPreview({
    setModalAlert,
  });

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (isAmAccessLoading) {
    return <FullPageLoader />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={isAmAccessDenied ? handleCloseAmAccessModal : handleCloseAlertModal}
      onSuccessModalClose={handleCloseSuccessModal}
    >
      <ApplicationHeader {...headerProps} />

      {isAmAccessDenied ? null : isApplicationLoading ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <GuaranteesManagerApplicationSidePanelLayout
            applicationId={effectiveApplicationId}
            onError={showGuaranteesRequestError}
          >
            <ManagerApprovalLetterDocumentSection
              coverageTypeOption={coverageTypeOption}
              coverageTypeOptions={coverageTypeOptions}
              isDocumentLoading={isDocumentLoading}
              isMainDealConditionsLoading={isMainDealConditionsLoading}
              isMainDealConditionsSaving={isMainDealConditionsSaving}
              isSubmitDisabled={isSubmitDisabled}
              isSubmitting={isSubmittingByAmManager}
              mainDealConditions={mainDealConditions}
              pdfUrl={pdfUrl}
              showCoverageTypeField={showCoverageTypeField}
              onCoverageTypeChange={handleCoverageTypeChange}
              onMainDealConditionsDirtyChange={handleMainDealConditionsDirtyChange}
              onMainDealConditionsSubmit={handleMainDealConditionsSubmit}
              onOpenPdf={handleOpenPdf}
              onSubmit={handleSubmitByAmManager}
            />
          </GuaranteesManagerApplicationSidePanelLayout>

          <GuaranteesManagerStepNavigator
            application={currentRouteApplication}
            applicationId={effectiveApplicationId}
            contragentId={contragentId}
            currentStepKey="amApprovalLetter"
          />
        </Box>
      )}
    </ApplicationPageFrame>
  );
};

export default ManagerApprovalLetterPage;
